// src/components/CompanyDetailsModal.jsx
import { useEffect, useState } from "react";
import axios from "axios";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  Clock,
} from "lucide-react";

// Use .env (VITE_API_BASE) or fall back to localhost for dev
const API = "https://superadmin.nissiindia.com";

export default function CompanyDetailsModal({
  open,
  tenantId,
  username,
  onClose,
}) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !tenantId || !username) return;

    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await axios.get(
          `${API}/api/mysql/tenant/${Number(tenantId)}/admins/${encodeURIComponent(
            String(username).trim()
          )}`
        );
        const r = data?.row ?? null;

        // Normalize is_active: handle 1/0, "1"/"0", Buffer([1]) from mysql2
        if (r) {
          const v = r.is_active;
          r.is_active =
            v === 1 ||
            v === true ||
            v === "1" ||
            (v && typeof v === "object" && (v[0] === 1 || v.data?.[0] === 1));
        }

        setRow(r);
      } catch (e) {
        console.error(e);
        setRow(null);
        setErr(e?.response?.data?.error || e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, tenantId, username]);

  if (!open) return null;

  const titleId = "company-details-title";
  const titleCompany = row?.company_name || "—";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* Modal card */}
      <div
        className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <h3 id={titleId} className="text-sm font-semibold text-slate-800">
            Company details — <span className="font-normal">{titleCompany}</span>
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-200/60 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[75vh] overflow-auto px-5 py-5">
          {loading ? (
            <div className="py-10 text-center text-slate-600">
              Loading company details…
            </div>
          ) : err ? (
            <div className="py-10 text-center text-rose-600">{err}</div>
          ) : !row ? (
            <div className="py-10 text-center text-slate-600">
              No record found for this user.
            </div>
          ) : (
            <div className="space-y-5">
              <AdminCard key={row.username} record={row} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** --- Admin card rendered like a read-only form --- */
function AdminCard({ record }) {
  const statusClass = record.is_active
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-rose-200";

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/90 shadow-sm">
      {/* Card head: username + chips */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white">
            <User className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">
              {record.username}
            </div>
            <div className="text-xs text-slate-500">
              {record.company_name || "—"}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${statusClass}`}
          >
            {record.is_active ? "Active" : "Inactive"}
          </span>
          {record.updated_at_utc && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
              <Clock className="h-3.5 w-3.5 text-slate-500" />
              {String(record.updated_at_utc).replace("T", " ").replace("Z", "")}
            </span>
          )}
        </div>
      </div>

      {/* Form-like fields */}
      <div className="p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Contact Name"
            icon={User}
            value={record.contact_name}
            placeholder="—"
          />
          <Field label="Email" icon={Mail} value={record.email} placeholder="—" />
          <Field label="Phone" icon={Phone} value={record.phone} placeholder="—" />
          <Field
            className="sm:col-span-2"
            label="Address"
            icon={MapPin}
            value={record.address}
            placeholder="—"
            long
          />
        </div>
      </div>
    </div>
  );
}

/** A read-only input-style field with label and optional icon */
function Field({
  label,
  icon: Icon,
  value,
  placeholder = "",
  long = false,
  className = "",
}) {
  const val = (value ?? "").toString().trim();
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </label>
      <div
        className={`relative ${
          long ? "min-h-[44px]" : ""
        } rounded-lg border border-slate-300 bg-white shadow-sm`}
      >
        {Icon && (
          <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        )}
        <div
          className={`${Icon ? "pl-9" : "pl-3"} pr-3 py-2 text-slate-900 placeholder:text-slate-400`}
        >
          {val || <span className="text-slate-400">{placeholder}</span>}
        </div>
      </div>
    </div>
  );
}
